/**
 * Landing page behaviour: carousel (autoplay, swipe, keyboard, count-ups), mobile nav drawer,
 * scroll-spy for the nav and scroll reveals. Ported from /LANDING PAGE/script.js and scoped to
 * the component root. Returns a cleanup so React (incl. StrictMode's double effects) leaves
 * no listeners, timers or observers behind.
 */
export default function initLanding(root) {
  if (!root) return undefined;
  const controller = new AbortController();
  const on = (target, type, fn, opts = {}) =>
    target.addEventListener(type, fn, { ...opts, signal: controller.signal });
  const frames = new Set();
  const observers = [];
  const cleanups = [];

  // Fall back gracefully where matchMedia is missing (old WebViews, jsdom in tests)
  const media = (query) =>
    typeof window.matchMedia === 'function'
      ? window.matchMedia(query)
      : { matches: false, addEventListener: () => {} };
  const reduceMotion = media('(prefers-reduced-motion: reduce)');
  const mobile = media('(max-width: 899px)');
  const hasObserver = typeof window.IntersectionObserver === 'function';

  /* ---------- Carousel ---------- */
  const carousel = root.querySelector('.carousel');
  if (carousel) {
    const AUTOPLAY_MS = 6000;
    const stage = carousel.querySelector('.carousel__stage');
    const visuals = [...carousel.querySelectorAll('.slide-visual')];
    const texts = [...carousel.querySelectorAll('.slide-text')];
    const dots = [...carousel.querySelectorAll('.dot')];
    const count = visuals.length;
    let index = 0;
    let timer = null;
    let hovering = false;
    let focused = false;
    let inView = false;

    carousel.style.setProperty('--autoplay', `${AUTOPLAY_MS}ms`);

    // Count numbers up from zero when a slide enters (keeps the final text for no-JS / reduced motion)
    const countUp = (root, baseDelay = 0) => {
      root.querySelectorAll('[data-count]').forEach((el, i) => {
        const target = parseFloat(el.dataset.count);
        const decimals = (el.dataset.count.split('.')[1] || '').length;
        const suffix = el.dataset.suffix || '';
        const fmt = (v) => `${v.toFixed(decimals)}${suffix}`;
        // Start counting as the row / tile carrying the number lands
        const host = el.closest('.standings__row, .player-card__stat');
        const hostDelay = host ? parseFloat(getComputedStyle(host).animationDelay) * 1000 || 0 : 0;
        const delay = baseDelay + hostDelay + i * 25;
        const duration = 900;
        el.textContent = fmt(0);
        let start = null;
        const tick = (t) => {
          if (start === null) start = t + delay;
          const p = Math.min(Math.max((t - start) / duration, 0), 1);
          const eased = 1 - Math.pow(1 - p, 3);
          el.textContent = fmt(target * eased);
          if (p < 1) frames.add(requestAnimationFrame(tick));
          else el.textContent = fmt(target);
        };
        frames.add(requestAnimationFrame(tick));
      });
    };

    const playEnter = (visual, cls) => {
      visual.classList.remove('enter-next', 'enter-prev');
      void visual.offsetWidth;
      visual.classList.add(cls);
      clearAnim(visual, [cls]);
      countUp(visual, 150);
    };

    const clearAnim = (el, classes) => {
      on(el, 'animationend', function done(e) {
        if (e.target !== el) return;
        el.classList.remove(...classes);
        el.removeEventListener('animationend', done);
      });
    };

    const goTo = (next, dir) => {
      next = (next + count) % count;
      if (next === index) return;
      dir = dir || (next > index ? 1 : -1);
      const prev = index;
      index = next;
      const animate = !reduceMotion.matches;
      const enter = dir > 0 ? 'enter-next' : 'enter-prev';
      const leave = dir > 0 ? 'leave-next' : 'leave-prev';
      const all = ['enter-next', 'enter-prev', 'leave-next', 'leave-prev'];

      // Visuals
      const out = visuals[prev];
      const inn = visuals[next];
      visuals.forEach((v) => v.classList.remove(...all));
      out.classList.remove('is-active');
      out.setAttribute('aria-hidden', 'true');
      inn.classList.add('is-active');
      inn.removeAttribute('aria-hidden');
      if (animate) {
        out.classList.add(leave);
        clearAnim(out, [leave]);
        playEnter(inn, enter);
      }

      // Texts
      const tOut = texts[prev];
      const tIn = texts[next];
      texts.forEach((t) => t.classList.remove('text-in', 'text-out'));
      tOut.classList.remove('is-active');
      tOut.setAttribute('aria-hidden', 'true');
      tIn.classList.add('is-active');
      tIn.removeAttribute('aria-hidden');
      if (animate) {
        tOut.classList.add('text-out');
        on(tOut.lastElementChild, 'animationend', () => tOut.classList.remove('text-out'), { once: true });
        tIn.classList.add('text-in');
        on(tIn.lastElementChild, 'animationend', () => tIn.classList.remove('text-in'), { once: true });
      }

      // Dots
      dots.forEach((d, i) => {
        const on = i === next;
        d.classList.toggle('is-active', on);
        d.setAttribute('aria-selected', String(on));
      });

      restart();
    };

    // Autoplay — paused while hovered, focused, off-screen, or for reduced motion
    const shouldPlay = () => inView && !hovering && !focused && !reduceMotion.matches && !document.hidden;
    function restart() {
      clearTimeout(timer);
      carousel.classList.remove('is-playing');
      void carousel.offsetWidth; // restart the dot progress animation
      if (shouldPlay()) {
        carousel.classList.add('is-playing');
        carousel.classList.remove('is-paused');
        timer = setTimeout(() => goTo(index + 1, 1), AUTOPLAY_MS);
      }
    }
    const pause = () => {
      clearTimeout(timer);
      if (carousel.classList.contains('is-playing')) carousel.classList.add('is-paused');
    };
    const resume = () => { if (shouldPlay()) restart(); };

    carousel.querySelectorAll('.icon-btn').forEach((btn) =>
      on(btn, 'click', () => goTo(index + Number(btn.dataset.dir), Number(btn.dataset.dir)))
    );
    dots.forEach((dot, i) => on(dot, 'click', () => goTo(i)));

    on(carousel, 'keydown', (e) => {
      if (e.key === 'ArrowRight') { e.preventDefault(); goTo(index + 1, 1); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); goTo(index - 1, -1); }
    });

    on(carousel, 'pointerenter', (e) => { if (e.pointerType === 'mouse') { hovering = true; pause(); } });
    on(carousel, 'pointerleave', (e) => { if (e.pointerType === 'mouse') { hovering = false; resume(); } });
    on(carousel, 'focusin', () => { focused = true; pause(); });
    on(carousel, 'focusout', (e) => {
      if (!carousel.contains(e.relatedTarget)) { focused = false; resume(); }
    });
    on(document, 'visibilitychange', () => (document.hidden ? pause() : resume()));

    // Swipe / drag on the visual
    let startX = null;
    let startY = 0;
    on(stage, 'pointerdown', (e) => { startX = e.clientX; startY = e.clientY; });
    on(stage, 'pointerup', (e) => {
      if (startX === null) return;
      const dx = e.clientX - startX;
      const dy = e.clientY - startY;
      startX = null;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) goTo(index + (dx < 0 ? 1 : -1), dx < 0 ? 1 : -1);
    });
    on(stage, 'pointercancel', () => { startX = null; });

    let introduced = false;
    const carouselIo = hasObserver && new IntersectionObserver(([entry]) => {
      inView = entry.isIntersecting;
      if (inView && !introduced && !reduceMotion.matches) {
        introduced = true;
        playEnter(visuals[index], 'enter-next');
      }
      inView ? resume() : pause();
    }, { threshold: 0.35 });
    if (carouselIo) {
      carouselIo.observe(carousel);
      observers.push(carouselIo);
    }
    cleanups.push(() => clearTimeout(timer));

  }

  /* ---------- Mobile navigation drawer ---------- */
  const burger = root.querySelector('.hamburger');
  const drawer = root.querySelector('.drawer');
  const setMenu = (open) => {
    root.classList.toggle('menu-open', open);
    document.body.style.overflow = open ? 'hidden' : '';
    burger.setAttribute('aria-expanded', String(open));
    burger.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
    drawer.setAttribute('aria-hidden', String(!open));
    if (open) drawer.querySelector('.drawer__item').focus({ preventScroll: true });
  };
  on(burger, 'click', () => setMenu(!root.classList.contains('menu-open')));
  on(drawer, 'click', (e) => {
    if (e.target.closest('[data-close], .drawer__item')) setMenu(false);
  });
  on(document, 'keydown', (e) => {
    if (e.key === 'Escape' && root.classList.contains('menu-open')) { setMenu(false); burger.focus(); }
  });
  on(mobile, 'change', (e) => { if (!e.matches) setMenu(false); });

  /* ---------- Active nav item follows scroll ---------- */
  const navLinks = [...root.querySelectorAll('.tab[data-target], .drawer__item[data-target]')];
  const sections = ['top', 'features', 'download', 'contact'].map((id) => root.querySelector(`#${id}`))
    .filter(Boolean); // some sections are hidden behind landing flags
  // Inside the app the page scrolls in a wrapper <div>, not the window, so find whichever
  // element actually scrolls and measure sections against the viewport.
  const findScroller = () => {
    for (let el = root.parentElement; el && el !== document.body; el = el.parentElement) {
      const { overflowY } = getComputedStyle(el);
      if (/(auto|scroll)/.test(overflowY) && el.scrollHeight > el.clientHeight + 1) return el;
    }
    return null; // the window scrolls
  };
  const setActive = () => {
    if (!sections.length) return;
    const scroller = findScroller();
    const line = window.innerHeight * 0.35;
    let current = sections[0];
    sections.forEach((s) => { if (s.getBoundingClientRect().top <= line) current = s; });
    const atBottom = scroller
      ? scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 4
      : window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 4;
    const scrolled = scroller ? scroller.scrollTop > 0 : window.scrollY > 0;
    if (atBottom && scrolled) current = sections[sections.length - 1];
    navLinks.forEach((l) => l.classList.toggle('is-active', l.dataset.target === current.id));
  };
  // Scroll events don't bubble, but a capturing listener on document sees every scroller's
  on(document, 'scroll', setActive, { passive: true, capture: true });
  on(window, 'resize', setActive, { passive: true });
  setActive();

  /* ---------- Scroll reveal ---------- */
  const revealEls = root.querySelectorAll('[data-reveal]');
  if (hasObserver) {
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-revealed');
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.15, rootMargin: '0px 0px -40px 0px' });
    observers.push(io);
    revealEls.forEach((el) => io.observe(el));
  } else {
    revealEls.forEach((el) => el.classList.add('is-revealed'));
  }


  return () => {
    controller.abort();
    frames.forEach((id) => cancelAnimationFrame(id));
    observers.forEach((o) => o.disconnect());
    cleanups.forEach((fn) => fn());
    root.classList.remove('menu-open');
    document.body.style.overflow = '';
  };
}
